import React from 'react';
import { useSelector } from 'react-redux';
import { matchRoutes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import withRouter from '@fuse/core/withRouter';
import FuseSplashScreen from '@fuse/core/FuseSplashScreen';

import AppContext from 'app/AppContext';
import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { selectUser } from 'app/store/userSlice';
import { useIsModuleDisabled } from 'app/main/components';

interface FuseAuthorizationProps {
	children: React.ReactNode;
}

function FuseAuthorization(props: FuseAuthorizationProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);
	const { routes } = React.useContext(AppContext);

	const user = useSelector(selectUser);

	const [isAuthorized, setIsAuthorized] = React.useState(false);

	React.useEffect(() => {
		const matchedRoute = matchRoutes(routes, location.pathname)?.[0];
		const isGuest = !user.roles || user.roles.length === 0;

		let accessGranted = true;
		let onlyGuests = false;

		if (matchedRoute?.route) {
			const { auth } = matchedRoute.route;

			if (auth === undefined) {
				accessGranted = !isGuest;
			} else if (auth === null) {
				accessGranted = isGuest;
				onlyGuests = true;
			} else {
				accessGranted = !isGuest && auth({ isModuleDisabled, authorizer });
			}
		}

		if (!accessGranted) {
			if (isGuest) {
				if (location.pathname !== '/login') {
					const currentApp = import.meta.env.VITE_UID;
					const redirectTo = location.pathname !== '/' ? encodeURIComponent(location.pathname) : undefined;
					const loginUrl = redirectTo ? `login?redirectApp=${currentApp}&redirectTo=${redirectTo}` : 'login';
					navigate(loginUrl, { replace: true });
				}
			} else if (!onlyGuests) {
				navigate('401');
			} else if (location.pathname === '/login') {
				const defaultLoginRedirectUrl = user.data.user_login_redirect_url ?? '/';
				const redirectTo = searchParams.get('redirectTo');
				navigate(redirectTo || defaultLoginRedirectUrl, { replace: true });
			}
		}

		setIsAuthorized(true);
	}, [authorizer, isModuleDisabled, location.pathname, navigate, routes, searchParams, user.data, user.roles]);

	if (!isAuthorized) {
		return <FuseSplashScreen />;
	}

	return props.children;
}

export default withRouter(FuseAuthorization);
